import { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, Coins } from "lucide-react";

// CoinPicker
// - Desktop: uses Popover (unchanged)
// - Mobile: uses a bottom Sheet with a full-height coin list
//
// `mobile` prop is driven by useIsMobile() in the parent — we don't call it
// here to keep this component pure (easier to test / reuse).

export default function CoinPicker({
  coins,
  value,
  onChange,
  disabledSymbol,
  side = "left",
  loading,
  mobile = false,
  testIdPrefix = "coin-picker",
}) {
  const [open, setOpen] = useState(false);
  const selected = coins.find((c) => c.symbol === value) || null;

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`cb-hud-chip flex items-center gap-3 hover:bg-white/5 transition-colors group focus:outline-none focus:ring-1 focus:ring-white/30 ${
        mobile ? "px-3 py-3 min-h-[52px] flex-1 min-w-0" : "px-4 py-3 min-w-[190px]"
      }`}
      data-testid={`${testIdPrefix}-trigger`}
      aria-expanded={open}
    >
      <CoinBadge coin={selected} loading={loading} size={mobile ? 28 : 32} />
      <div className="flex flex-col items-start leading-tight flex-1 min-w-0">
        <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-500">
          {side === "left" ? "Bulls" : "Bears"}
        </span>
        <span
          className={`font-display font-bold tracking-tight text-white truncate max-w-full ${mobile ? "text-base" : "text-lg"}`}
          data-testid={`${testIdPrefix}-value`}
        >
          {selected ? selected.symbol : loading ? "…" : "Pick"}
        </span>
      </div>
      <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors shrink-0" />
    </button>
  );

  if (mobile) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="!bg-[#010308]/95 !border-t !border-white/10 !rounded-none !p-0 h-[85vh] flex flex-col"
            data-testid={`${testIdPrefix}-sheet`}
          >
            <SheetTitle className="sr-only">Pick a coin</SheetTitle>
            <div className="px-4 pt-4 pb-2 border-b border-white/10 flex items-center justify-between">
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-slate-400">
                {side === "left" ? "Pick Bulls asset" : "Pick Bears asset"}
              </span>
            </div>
            <CoinCommand
              coins={coins}
              value={value}
              disabledSymbol={disabledSymbol}
              onSelect={(sym) => {
                onChange?.(sym);
                setOpen(false);
              }}
              testIdPrefix={testIdPrefix}
              autoFocusSearch
              listClassName="max-h-none flex-1"
            />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  // Desktop popover
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={side === "left" ? "start" : "end"}
        className="p-0 w-[300px] border-white/10 bg-[#010308]/95 backdrop-blur-xl max-h-[60vh] overflow-hidden flex flex-col"
        data-testid={`${testIdPrefix}-popover`}
      >
        <CoinCommand
          coins={coins}
          value={value}
          disabledSymbol={disabledSymbol}
          onSelect={(sym) => {
            onChange?.(sym);
            setOpen(false);
          }}
          testIdPrefix={testIdPrefix}
          listClassName="max-h-none flex-1 overflow-y-auto"
        />
      </PopoverContent>
    </Popover>
  );
}

function CoinCommand({ coins, value, disabledSymbol, onSelect, testIdPrefix, autoFocusSearch, listClassName = "max-h-none flex-1" }) {
  return (
    <Command
      filter={(itemValue, search) => (itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
      className="bg-transparent flex-1 min-h-0"
    >
      <CommandInput
        placeholder="Search symbol or name…"
        className="font-mono-tac text-sm"
        data-testid={`${testIdPrefix}-search`}
        autoFocus={autoFocusSearch}
      />
      <CommandList className={listClassName} data-testid={`${testIdPrefix}-list`}>
        <CommandEmpty className="font-mono-tac text-xs text-slate-500 py-6">
          No coins found.
        </CommandEmpty>
        <CommandGroup>
          {coins.map((c) => {
            const isDisabled = disabledSymbol && disabledSymbol === c.symbol;
            return (
              <CommandItem
                key={c.symbol}
                value={`${c.symbol} ${c.name}`}
                disabled={isDisabled}
                onSelect={() => {
                  if (isDisabled) return;
                  onSelect?.(c.symbol);
                }}
                className="gap-3 cursor-pointer data-[selected=true]:bg-white/10 data-[disabled=true]:opacity-40 min-h-[44px]"
                data-testid={`${testIdPrefix}-item-${c.symbol}`}
              >
                <CoinBadge coin={c} size={26} />
                <div className="flex flex-col leading-tight flex-1 min-w-0">
                  <span className="font-mono-tac text-sm text-white font-bold tracking-wide">
                    {c.symbol}
                  </span>
                  <span className="font-mono-tac text-[10px] text-slate-500 truncate">
                    {c.name}
                  </span>
                </div>
                {c.symbol === value && (
                  <span className="font-mono-tac text-[9px] uppercase tracking-[0.24em] text-[#00FFA3]">
                    · Live
                  </span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function CoinBadge({ coin, size = 32, loading }) {
  const [err, setErr] = useState(false);
  const dim = { width: size, height: size };
  const wrap = "flex items-center justify-center rounded-full bg-black/40 border border-white/10 shrink-0 overflow-hidden";
  if (loading) {
    return (
      <div className={wrap} style={dim}>
        <div className="w-3 h-3 rounded-full bg-white/20 animate-pulse" />
      </div>
    );
  }
  if (!coin) {
    return (
      <div className={wrap} style={dim}>
        <Coins className="w-4 h-4 text-slate-500" />
      </div>
    );
  }
  if (coin.icon && !err) {
    return (
      <div className={wrap} style={dim}>
        <img
          src={coin.icon}
          alt={coin.symbol}
          width={size}
          height={size}
          onError={() => setErr(true)}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div className={wrap} style={dim}>
      <span className="font-mono-tac text-[10px] font-bold tracking-tight text-white">
        {coin.symbol.slice(0, 3)}
      </span>
    </div>
  );
}
